import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Toggle from "components/ui/toggle/Toggle";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { SubscriptionEnum } from "lib/types/graphQLEnums";
import { PlanActionButton } from "./SubscriptionButtons";
import {
  BID_GROWTH_CARD_PRICE_INFO,
  CUSTOM_PRICING_INFO,
  FREE_PRICE_INFO,
  StudioType,
} from "./subscriptionPlanDetailsConfig";

export type BillingVariant = {
  priceInfo: string;
  billingFrequency?: string;
};

type PlanConfig = {
  targetAudience: string;
  description: string;
  features: string[];
  monthly?: BillingVariant;
  annual?: BillingVariant;
};

type StudioConfig = {
  freemium: PlanConfig;
  basic?: PlanConfig;
  growth: PlanConfig;
  expert: PlanConfig;
};

export function resolveVariant(config: PlanConfig, isAnnual: boolean): BillingVariant | undefined {
  if (isAnnual) {
    return config.annual ?? config.monthly;
  }
  return config.monthly ?? config.annual;
}

export const STUDIO_CONFIGS: Record<StudioType, StudioConfig> = {
  sales: {
    freemium: {
      targetAudience: "to get started",
      description:
        "Automatically find tender opportunities from every public sector portal in one place.",
      features: ["Open tenders", "Pre tenders"],
      monthly: { priceInfo: FREE_PRICE_INFO },
    },
    basic: {
      targetAudience: "for individuals",
      description:
        "Start creating qualified pipeline earlier. Forecast upcoming expiring contracts and pre-empt renewals.",
      features: [
        "Upcoming expiring contracts",
        "Historic contract awards",
        "Live chat support",
        "Plan highlight",
      ],
      monthly: { priceInfo: "From £150", billingFrequency: "month" },
      annual: { priceInfo: "From £99", billingFrequency: "month" },
    },
    growth: {
      targetAudience: "for small teams",
      description:
        "Boost next-level pipeline & routes to market with earlier buyer intent, framework intel and new contacts.",
      features: [
        "Buyer and supplier intelligence",
        "Core customer success support",
        "Deep-dive data like frameworks, documents and spend",
        "Decision maker contacts",
      ],
      monthly: { priceInfo: "From £475", billingFrequency: "month" },
    },
    expert: {
      targetAudience: "for large teams",
      description:
        "Boost next-level pipeline & routes to market with earlier buyer intent, framework intel and new contacts.",
      features: [
        "Market analysis suite",
        "Advanced Customer support, advisory and enablement",
        "Multi-team configuration",
        "Integration support",
      ],
      monthly: { priceInfo: CUSTOM_PRICING_INFO },
    },
  },
  bid: {
    freemium: {
      targetAudience: "to get started",
      description:
        "Automatically find relevant tenders from every public sector portal in one place and use AI to qualify them in seconds.",
      features: [
        "3 AI Bid / No-bid reports",
        "Tender tracking + alerts",
        "Self-serve customer support",
      ],
      monthly: { priceInfo: FREE_PRICE_INFO },
    },
    growth: {
      targetAudience: "for small teams",
      description:
        "Save hours with AI powered qualification, project collaboration and auto-generated draft responses, fuelled by your bid library & our market data.",
      features: [
        "15 AI Bid / No-bid reports per month",
        "5 bid projects per month",
        "Collaborator seat availability",
        "Secure, hosted bid content library",
        "AI responses powered by Stotles market data",
        "Dedicated customer success - Gold tier",
      ],
      monthly: { priceInfo: BID_GROWTH_CARD_PRICE_INFO, billingFrequency: "month" },
    },
    expert: {
      targetAudience: "for large teams",
      description:
        "Scale your revenue engine with tools to organise large-scale bid processes, unlimited AI bid qualification and uncapped response generation.",
      features: [
        "Unlimited AI Bid / No-bid reports per month",
        "Unlimited active bid projects",
        "Enterprise SAML/Single sign-on",
        "Multi-team instances & configurations",
        "Dedicated customer success - Platinum tier",
      ],
      monthly: { priceInfo: CUSTOM_PRICING_INFO },
    },
  },
};

export default function SubscriptionPlanCards({
  studioType,
  triggerBookADemoForm,
  triggerDowngradeModal,
  isAnnual = true,
  onIsAnnualChange,
}: {
  studioType: StudioType;
  triggerBookADemoForm: (value: boolean) => void;
  triggerDowngradeModal: (value: boolean) => void;
  isAnnual?: boolean;
  onIsAnnualChange?: (value: boolean) => void;
}) {
  const currentUser = useAssertCurrentUser();
  const currentPlan = currentUser.subscription;

  const studioConfig = STUDIO_CONFIGS[studioType];

  const freemiumVariant = resolveVariant(studioConfig.freemium, isAnnual);
  const basicVariant = studioConfig.basic
    ? resolveVariant(studioConfig.basic, isAnnual)
    : undefined;
  const growthVariant = resolveVariant(studioConfig.growth, isAnnual);
  const expertVariant = resolveVariant(studioConfig.expert, isAnnual);

  return (
    <Plans gap={16}>
      <Plan
        title="Free"
        subTitle={studioConfig.freemium.targetAudience}
        priceInfo={freemiumVariant?.priceInfo ?? ""}
        billingFrequency={freemiumVariant?.billingFrequency}
        description={studioConfig.freemium.description}
        featurePromo="Free includes:"
        features={studioConfig.freemium.features}
        // For now, we hardcode bid studioType to show free plan as current plan
        isCurrentPlan={currentPlan === "freemium" || studioType === "bid"}
        cta={
          <PlanActionButton
            plan={SubscriptionEnum.Freemium}
            currentPlan={studioType === "bid" ? SubscriptionEnum.Freemium : currentPlan}
            triggerBookADemoForm={triggerBookADemoForm}
            triggerDowngradeModal={triggerDowngradeModal}
          />
        }
      />
      {studioConfig.basic && basicVariant && (
        <Plan
          title="Basic"
          subTitle={studioConfig.basic.targetAudience}
          priceInfo={basicVariant.priceInfo}
          billingFrequency={basicVariant.billingFrequency}
          description={studioConfig.basic.description}
          featurePromo="Everything from Free plus:"
          features={studioConfig.basic.features}
          isCurrentPlan={studioType !== "bid" && currentPlan === SubscriptionEnum.Basic}
          toggle={
            studioConfig.basic.monthly && studioConfig.basic.annual ? (
              <AnnualToggle checked={isAnnual} onChange={onIsAnnualChange} />
            ) : undefined
          }
          cta={
            <PlanActionButton
              plan={SubscriptionEnum.Basic}
              currentPlan={studioType === "bid" ? SubscriptionEnum.Freemium : currentPlan}
              triggerBookADemoForm={triggerBookADemoForm}
              triggerDowngradeModal={triggerDowngradeModal}
              annualPlan={isAnnual}
            />
          }
        />
      )}
      <Plan
        title="Growth"
        subTitle={studioConfig.growth.targetAudience}
        priceInfo={growthVariant?.priceInfo ?? ""}
        billingFrequency={growthVariant?.billingFrequency}
        description={studioConfig.growth.description}
        featurePromo="Everything from Basic plus:"
        features={studioConfig.growth.features}
        isCurrentPlan={studioType !== "bid" && currentPlan === SubscriptionEnum.Growth}
        cta={
          <PlanActionButton
            plan={SubscriptionEnum.Growth}
            currentPlan={studioType === "bid" ? SubscriptionEnum.Freemium : currentPlan}
            triggerBookADemoForm={triggerBookADemoForm}
            triggerDowngradeModal={triggerDowngradeModal}
          />
        }
      />
      <Plan
        title="Expert"
        subTitle={studioConfig.expert.targetAudience}
        priceInfo={expertVariant?.priceInfo ?? ""}
        billingFrequency={expertVariant?.billingFrequency}
        description={studioConfig.expert.description}
        featurePromo="Everything in Growth, plus:"
        features={studioConfig.expert.features}
        isCurrentPlan={studioType !== "bid" && currentPlan === SubscriptionEnum.Expert}
        cta={
          <PlanActionButton
            plan={SubscriptionEnum.Expert}
            currentPlan={studioType === "bid" ? SubscriptionEnum.Freemium : currentPlan}
            triggerBookADemoForm={triggerBookADemoForm}
            triggerDowngradeModal={triggerDowngradeModal}
          />
        }
      />
    </Plans>
  );
}

type PlanProps = {
  title: "Free" | "Basic" | "Growth" | "Expert";
  subTitle: string;
  description: string;
  priceInfo: string;
  billingFrequency?: string;
  featurePromo: string;
  features: string[];
  isCurrentPlan: boolean;
  cta?: JSX.Element;
  toggle?: React.ReactNode;
};

function Plan({
  title,
  subTitle,
  description,
  priceInfo,
  billingFrequency,
  featurePromo,
  features,
  isCurrentPlan,
  cta,
  toggle,
}: PlanProps) {
  return (
    <PlanCard isCurrentPlan={isCurrentPlan}>
      <Summary>
        <Flex flexDirection="column" gap={16}>
          <Flex alignItems="baseline" gap={8}>
            <Text h2>{title}</Text>
            <Subtitle>{subTitle}</Subtitle>
          </Flex>
          <DescriptionText>{description}</DescriptionText>
        </Flex>
        <SummaryBottom>
          <Flex alignItems="baseline" gap={5}>
            <Text h1>{priceInfo}</Text>
            {billingFrequency && (
              <BillingFrequencyText>{` / ${billingFrequency}`}</BillingFrequencyText>
            )}
          </Flex>
          <ToggleRow>{toggle}</ToggleRow>
          {cta && cta}
        </SummaryBottom>
      </Summary>
      <Features>
        <PromoText>{featurePromo}</PromoText>
        {features.map((feature) => (
          <Flex key={feature} gap={4} alignItems="flex-start">
            <UIcon icon="checkOutlined" size={16} color="sysPrimaryDefault" />
            <Text>{feature}</Text>
          </Flex>
        ))}
      </Features>
    </PlanCard>
  );
}

const Plans = styled(Flex)({
  "@media (max-width: 1320px)": {
    flexDirection: "column",
    alignItems: "stretch",
  },
});

const PlanCard = styled(Flex)<{ isCurrentPlan: boolean }>(({ theme, isCurrentPlan }) => ({
  flexDirection: "column",
  alignItems: "flex-start",
  flex: "1 0 0",
  alignSelf: "stretch",
  borderRadius: "12px",
  border: `${isCurrentPlan ? "4px" : "1px"} solid ${isCurrentPlan ? theme.colors.sysPrimaryDefault : theme.colors.sysBorderDecorative}`,
  "@media (max-width: 1320px)": {
    flexDirection: "row",
    alignItems: "stretch",
  },
}));

const Summary = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 16,
  padding: 24,
  width: "100%",
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  "@media (max-width: 1320px)": {
    borderBottom: "none",
    borderRight: `1px solid ${theme.colors.sysBorderDecorative}`,
  },
}));

const SummaryBottom = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  marginTop: "auto",
});

const Features = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  padding: 24,
  width: "100%",
});

const Subtitle = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextPlaceholder,
  fontStyle: "italic",
}));

const DescriptionText = styled(Text)({
  minHeight: 85,
});

const PromoText = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextPlaceholder,
}));

const BillingFrequencyText = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
}));

function AnnualToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange?: (checked: boolean) => void;
}) {
  return (
    <Flex alignItems="center" gap={8}>
      <Toggle
        checked={checked}
        onCheckedChange={onChange}
        data-testid="subscription-annual-billing-toggle"
      />
      <Text color="sysTextSecondary">{checked ? "Billed annually" : "Billed monthly"}</Text>
    </Flex>
  );
}

const ToggleRow = styled(Flex)({
  height: 22,
  alignItems: "center",
});
